// Epic 10 Showcase — the tiny drag-and-drop protocol shared by the Collection
// grid (drag source for "add to showcase") and the Showcase pane (drag source
// for reorder, drop target for both). A custom MIME type keeps these drags
// from being confused with any other draggable on the page, and lets the drop
// handlers branch on where the drag came from.

import type { DragEvent } from "react";

import type { ShowcaseItemKind } from "./useShowcase";

export const SHOWCASE_DND_MIME = "application/x-greenhold-showcase";

export interface ShowcaseDragPayload {
  /** "grid" → a collection item dragged in to be published; "pane" → an item
   *  already in the showcase being dragged to a new position. */
  source: "grid" | "pane";
  kind: ShowcaseItemKind;
  id: string;
  /** The pane index of a "pane"-source drag (the reorder-from position). */
  index?: number;
}

export function writeShowcaseDrag(
  event: DragEvent,
  payload: ShowcaseDragPayload,
): void {
  event.dataTransfer.setData(SHOWCASE_DND_MIME, JSON.stringify(payload));
  event.dataTransfer.effectAllowed = "move";
}

export function readShowcaseDrag(event: DragEvent): ShowcaseDragPayload | null {
  try {
    const raw = event.dataTransfer.getData(SHOWCASE_DND_MIME);
    return raw ? (JSON.parse(raw) as ShowcaseDragPayload) : null;
  } catch {
    return null;
  }
}

/** True when the current drag carries our payload — used in dragover to decide
 *  whether to preventDefault (which is what actually enables a drop). The
 *  payload itself is unreadable during dragover for security, so we sniff the
 *  advertised types instead. */
export function isShowcaseDrag(event: DragEvent): boolean {
  return Array.from(event.dataTransfer.types).includes(SHOWCASE_DND_MIME);
}
